import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { toast } from "react-toastify";
import { Column } from "primereact/column";
import { SplitButton } from "primereact/splitbutton";
import { useCallback, useEffect, useState } from "react";
import {
	Button,
	Container,
	OverlayTrigger,
	Row,
	Spinner,
	Tooltip,
} from "react-bootstrap";
import { Modal } from "../../../../../../components/modal";
import { PaginationList } from "../../../../../../components/pagination";
import { TreeTableProductsList } from "../../../../components/table-products";
import { NotFound, ContentBody } from "../../../../styled";

import { FaEllipsisH, FaFolder } from "react-icons/fa";
import { MdInfoOutline, MdOpenInNew } from "react-icons/md";
import { FiltersProps } from "../../../../../../@types";
import {
	managerProductActivate,
	managerProductCount,
	managerProductInactivate,
	managerProductRemove,
	productUnpublish,
} from "../../../../../../services/manager.service";
import {
	getMaterials,
	userMaterialsProductLeaf,
} from "../../../../../../services/manager/materials.service";
import {
	changeStatusLink,
	deleteLink,
} from "../../../../../../services/favorites.service";
import { FilterButton } from "./styles";

/** Componente que lista os materiais utilizados por determinado usuário
 *
 *
 */
export const UserMaterials = ({ userId, userType }: any): JSX.Element => {
	const history = useHistory();
	const { t: translation } = useTranslation();

	const [selectedNode, setSelectedNode] = useState<any>();
	const [expandedKeys, setExpandedKeys] = useState({});

	const [filters, setFilters] = useState<FiltersProps>({ page: 1 });
	const [loading, setLoading] = useState<boolean>(false);
	const [loadingModal, setLoadingModal] = useState<boolean>(false);
	const [statusFilter, setStatusFilter] = useState<
		"" | "active" | "inactive"
	>("");

	// Modal
	const [showInactivateLinkModalConfirm, setShowInactivateLinkModalConfirm] =
		useState<boolean>(false);
	const [showDeleteLinkModalConfirm, setShowDeleteLinkModalConfirm] =
		useState<boolean>(false);
	const [
		showInactivateMaterialModalConfirm,
		setShowInactivateMaterialModalConfirm,
	] = useState<boolean>(false);
	const [showDeleteMaterialModalConfirm, setShowDeleteMaterialModalConfirm] =
		useState<boolean>(false);
	const [showUnpublishModalConfirm, setUnpublishModalConfirm] =
		useState<boolean>(false);

	//Pagination
	const [totalItens, setTotalItens] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);

	//Ordering
	const [sortOrder, setSortOrder] = useState();
	const [sortField, setSortField] = useState();
	const [isSorting, setIsSorting] = useState(false);

	const [locationList, setLocationList] = useState<any[]>([]);
	const [showEmptyMessage, setShowEmptyMessage] = useState<boolean>(false);

	const handleTableSort = useCallback((event: any) => {
		let order =
			event.sortOrder < 0 ? "-" + event.sortField : event.sortField;

		setSortOrder(event.sortOrder);
		setSortField(event.sortField);
		setFilters({ ...filters, page: currentPage, ordering: order });
	}, []);

	const handlePageChange = (event?: any) => {
		window.scrollTo(0, 0);
		setFilters({ ...filters, page: event });
		setCurrentPage(event);
	};

	/**
	 * Atualiza a lista de materiais e mostra uma toast com a mensagem indicada
	 */
	const handleUpdateList = (message: string) => {
		getMaterials(
			userId,
			filters.page,
			userType === `Parceiro`,
			statusFilter
		)
			.then((data: any) => {
				if (data.results.length === 0) {
					setShowEmptyMessage(true);
				} else {
					setShowEmptyMessage(false);
				}
				setLocationList(treatNodes(data.results));
				setTotalItens(data.count);
				setLoading(false);
				toast.success(message);
			})
			.catch(() => setLoading(false))
			.finally(() => {
				// fechar modais caso o método tenha sido chamado por um
				setLoadingModal(false);
				setShowDeleteLinkModalConfirm(false);
				setShowDeleteMaterialModalConfirm(false);
				setShowInactivateLinkModalConfirm(false);
				setShowInactivateMaterialModalConfirm(false);
				setUnpublishModalConfirm(false);
				// fechar os nodes da tabela
				setExpandedKeys({});
			});
	};

	/**
	 * Trata a resposta da API para o formato esperado pela ProductsTreeTable
	 * @param infos campo results da resposta da API
	 * @returns nodes prontos para a TreeTable
	 */
	const treatNodes = (infos: any) => {
		let nodes: any = [];

		infos.map((node: any, index: number) => {
			let n = {
				key: node.key,
				data: node.data,
				leaf: !node.data.leaf,
			};

			nodes.push(n);
		});

		return nodes;
	};

	let treeTableConfig: any = [];

	if (userType === "Parceiro") {
		treeTableConfig = [
			{
				field: "title",
				header: "Nome",
				className: "sm-text",
				nameTemplate: true,
				expander: true,
				sortable: true,
				style: { textAlign: "left", width: "35%" },
			},
			{
				field: "used_materials",
				className: "d-none-xs-table d-none-md-table",
				header: "Utilização",
				sortable: false,
				style: { textAlign: "center" },
			},
			{
				field: "external_access",
				className: "d-none-xs-table d-none-md-table",
				header: "Acessos Externos",
				sortable: true,
				style: { textAlign: "center" },
			},
			{
				field: "product_origin",
				className: "d-none-xs-table d-none-md-table d-none-lg-table",
				header: "Origem",
				sortable: true,
				style: { textAlign: "center" },
			},
		];
	} else if (userType === "Assinante") {
		treeTableConfig = [
			{
				field: "title",
				header: "Nome",
				className: "sm-text",
				nameTemplate: true,
				expander: true,
				sortable: true,
				style: { textAlign: "left", width: "35%" },
			},
			{
				field: "responsible",
				className: "d-none-xs-table d-none-md-table",
				header: "Parceiro",
				sortable: false,
				style: { textAlign: "center" },
			},
			{
				field: "code",
				className: "d-none-xs-table d-none-md-table d-none-lg-table",
				header: "Código",
				sortable: true,
				codeTemplate: true,
				style: { textAlign: "center" },
			},
			{
				field: "external_access",
				className: "d-none-xxs-table",
				header: "Acessos Externos",
				sortable: true,
				style: { textAlign: "center" },
			},
		];
	} else {
		treeTableConfig = [
			{
				field: "title",
				header: "Nome",
				className: "sm-text",
				nameTemplate: true,
				expander: true,
				sortable: true,
				style: { textAlign: "left", width: "35%" },
			},
			{
				field: "responsible",
				className: "d-none-xs-table d-none-md-table",
				header: "Parceiro",
				sortable: false,
				style: { textAlign: "center" },
			},
			{
				field: "code",
				className: "d-none-xs-table d-none-md-table d-none-lg-table",
				header: "Código",
				sortable: true,
				codeTemplate: true,
				style: { textAlign: "center" },
			},
		];
	}

	/**
	 * Load inicial
	 */
	useEffect(() => {
		setLoading(true);
		getMaterials(
			userId,
			filters.page,
			userType === "Parceiro",
			statusFilter
		)
			.then((data: any) => {
				if (data.results.length === 0) {
					setShowEmptyMessage(true);
				} else {
					setShowEmptyMessage(false);
				}
				setLocationList(treatNodes(data.results));
				setTotalItens(data.count);
			})
			.finally(() => {
				setLoading(false);
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Atualizar listagem quando mudo filtro de ativo ou paginacao
	 */
	useEffect(() => {
		if (filters && filters.page && !isSorting) {
			setIsSorting(true);
			getMaterials(
				userId,
				filters.page,
				userType === "Parceiro",
				statusFilter
			)
				.then((data: any) => {
					if (data.results.length === 0) {
						setShowEmptyMessage(true);
					} else {
						setShowEmptyMessage(false);
					}
					setLocationList(treatNodes(data.results));
					setTotalItens(data.count);
				})
				.finally(() => {
					setLoading(false);
					setIsSorting(false);
					setExpandedKeys({});
				});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, statusFilter]);

	/**
	 * Lazy-load dos itens da tabela de materiais do cliente, disparado quando um node é "aberto"
	 */
	const handleTableNodeExpanded = (event: any) => {
		if (!event.node.children) {
			setIsSorting(true);

			userMaterialsProductLeaf(
				userId,
				userType === "Parceiro",
				event.node.data.type.toLowerCase(),
				event.node.data.id
			)
				.then((response: any) => {
					let children: any = treatNodes(response);
					let list = locationList;

					if (event.node.data.type === "Discipline") {
						//Acha o index do event.node --- Se o node for um children e não estiver na lista principal da pagina,
						//findIndex sempre retorna -1, ocorrendo em um bug de duplicação do node
						let i = list.findIndex(
							(no) => no.key === event.node.key
						);

						list[i].children = children;
						setLocationList(list);
					} else if (
						event.node.data.type === "Item" ||
						event.node.data.type === "Theme"
					) {
						const findNode = list.findIndex(
							(node) => node.key === event.node.key
						);

						if (findNode >= 0) {
							list[findNode].children = children;
						} else {
							// Adiciona as children no event.node
							event.node.children = children;
						}
					}

					setLocationList(list);
				})
				.finally(() => {
					setIsSorting(false);
				});
		}
	};

	const handleChangeStatusLink = (node: any) => {
		setSelectedNode(node.data);
		if (node.data.is_active) {
			setShowInactivateLinkModalConfirm(true);
		} else {
			changeStatusLink(node.data.id, userId, true, node.data.type)
				.then(() => {
					handleUpdateList("Link ativado com sucesso!");
					setSelectedNode(null);
				})
				.catch(() => {
					toast.error(
						"Ocorreu um erro ao ativar este link. Tente novamente."
					);
				});
		}
	};

	function handleConfirmLinkInactivate() {
		if (selectedNode.id) {
			setLoadingModal(true);
			changeStatusLink(selectedNode.id, userId, false, selectedNode.type)
				.then(() => {
					handleUpdateList("Link inativado com sucesso!");
					setSelectedNode(null);
				})
				.catch(() => {
					toast.error(
						"Ocorreu um erro ao inativar este link. Tente novamente."
					);
					setLoadingModal(false);
					setShowInactivateLinkModalConfirm(false);
				});
		}
	}

	const handleDeleteLink = (node: any) => {
		setSelectedNode(node.data);
		setShowDeleteLinkModalConfirm(true);
	};

	function handleConfirmLinkDelete() {
		if (selectedNode.id) {
			setLoadingModal(true);
			deleteLink(
				selectedNode.id,
				userId,
				selectedNode.is_active,
				selectedNode.type
			)
				.then((data) => {
					handleUpdateList("Link excluído com sucesso!");
					setShowDeleteLinkModalConfirm(false);
					setLoadingModal(false);
					setSelectedNode(null);
				})
				.catch(() => setLoadingModal(false));
		}
	}

	const handleMaterialActive = (node: any, activate: boolean = false) => {
		setSelectedNode(node.data);

		if (!activate) {
			setShowInactivateMaterialModalConfirm(true);
		} else if (activate) {
			managerProductActivate(node.data.id, node.data.type)
				.then((data) => {
					handleUpdateList("Material ativado com sucesso!");
					setSelectedNode(null);
				})
				.catch(() => {
					toast.error("Erro ao ativar o material!");
				});
		}
	};

	function handleConfirmMaterialInactivate() {
		if (selectedNode.id) {
			setLoadingModal(true);
			managerProductInactivate(selectedNode.id, selectedNode.type)
				.then((data) => {
					handleUpdateList("Material inativado com sucesso!");

					setSelectedNode(null);
				})
				.catch(() => {
					setLoadingModal(false);
					setShowInactivateMaterialModalConfirm(false);
				});
		}
	}

	const handleDeleteMaterial = (node: any) => {
		setSelectedNode(node.data);
		setShowDeleteMaterialModalConfirm(true);
	};

	function handleConfirmMaterialDelete() {
		if (selectedNode.id) {
			setLoadingModal(true);
			managerProductRemove(selectedNode.id, selectedNode.type)
				.then((data) => {
					handleUpdateList("Material excluído com sucesso!");
					setShowDeleteMaterialModalConfirm(false);
					setLoadingModal(false);
					setSelectedNode(null);
				})
				.catch(() => setLoadingModal(false));
		}
	}

	function actionTemplate(node: any) {
		let items: any = [];
		let id = node.data.id;

		items.push({
			label: translation("manager.links.toViewLinkLabel"),
			command: () => {
				history.push(
					"/manager/material/" +
						node.data.type.toLowerCase() +
						"/" +
						id
				);
			},
		});

		if (node.data.is_active) {
			if (userType === "Parceiro") {
				items.push({
					label: translation("manager.products.inactivateLabel"),
					command: () => {
						handleMaterialActive(node, false);
					},
				});
			} else {
				items.push({
					label: translation("manager.links.inactivateLinkLabel"),
					command: () => {
						handleChangeStatusLink(node);
					},
				});
			}
		} else {
			if (userType === "Parceiro") {
				items.push({
					label: translation("manager.products.reactivateLabel"),
					command: () => {
						handleMaterialActive(node, true);
					},
				});
				items.push({
					label: translation("manager.products.removeLabel"),
					command: () => {
						handleDeleteMaterial(node);
					},
				});
			} else {
				items.push({
					label: translation("manager.links.reactivateLinkLabel"),
					command: () => {
						handleChangeStatusLink(node);
					},
				});
				items.push({
					label: translation("manager.links.removeLinkLabel"),
					command: () => {
						handleDeleteLink(node);
					},
				});
			}
		}

		return (
			<OverlayTrigger
				placement="top"
				overlay={<Tooltip>Opções</Tooltip>}
			>
				<div>
					<SplitButton
						label=""
						className="p-button p-component p-button-rounded p-button-text p-button-icon-only"
						buttonClassName="d-none"
						dropdownIcon={<FaEllipsisH />}
						model={items}
					/>
				</div>
			</OverlayTrigger>
		);
	}

	function detailsTemplate(node: any) {
		let id = node.data.id;
		return (
			<OverlayTrigger
				overlay={
					<Tooltip id="tooltip-disabled">Visualizar Material</Tooltip>
				}
			>
				<Button
					variant="link"
					type="button"
					onClick={() =>
						history.push(
							"/manager/material/" +
								node.data.type.toLowerCase() +
								"/" +
								id
						)
					}
				>
					<MdOpenInNew size="26" />
				</Button>
			</OverlayTrigger>
		);
	}

	function knowledgesTemplate(node: any) {
		return (
			<>
				{node?.data.knowledge_area?.length ? (
					node?.data.knowledge_area?.map(
						(knowledge: any, index: number) => (
							<>
								{knowledge +
									(node?.data.knowledge_area?.length > 1
										? node?.data.knowledge_area?.length ===
										  index + 1
											? " "
											: ", "
										: " ")}
							</>
						)
					)
				) : (
					<></>
				)}
			</>
		);
	}

	// publish / unpublish functions
	function publishTemplate(node: any) {
		return (
			<>
				{node.data.status === 2 ? (
					<>
						<Button
							variant="link"
							style={{ textDecoration: "none" }}
							type="button"
							onClick={() => handleProductUnpublish(node)}
						>
							Despublicar
						</Button>
					</>
				) : (
					<>
						{node.data.reason ? (
							<>
								<OverlayTrigger
									overlay={
										<Tooltip id="tooltip-disabled">
											{translation(
												"manager.labels." +
													node.data.type.toLowerCase() +
													"." +
													node?.data?.reason
											)}
										</Tooltip>
									}
								>
									<span className="d-inline-block">
										<Button
											variant="link"
											style={{ textDecoration: "none" }}
											type="button"
											disabled={!node.data.can_publish}
											onClick={() =>
												handleProductPublish(node.data)
											}
										>
											Publicar
											{!node.data.can_publish &&
											node.data.reason ? (
												<>
													<MdInfoOutline
														style={{
															marginLeft: "5px",
														}}
													></MdInfoOutline>
												</>
											) : (
												<></>
											)}
										</Button>
									</span>
								</OverlayTrigger>
							</>
						) : (
							<>
								<Button
									variant="link"
									style={{ textDecoration: "none" }}
									type="button"
									disabled={!node.data.can_publish}
									onClick={() =>
										handleProductPublish(node.data)
									}
								>
									Publicar
									{!node.data.can_publish &&
									node.data.reason ? (
										<>
											<MdInfoOutline
												style={{ marginLeft: "5px" }}
											></MdInfoOutline>
										</>
									) : (
										<></>
									)}
								</Button>
							</>
						)}
					</>
				)}
			</>
		);
	}

	const handleProductPublish = (node: any) => {
		// productPublish(node.id, node.type).then(resp => {
		//     handleUpdateList(resp, 'Material publicado com sucesso!');
		// }).catch(() => {
		//     toast.error('Erro ao publicado o material!');
		// })
	};

	const handleProductUnpublish = (node: any) => {
		managerProductCount(node.data.id, node.data.type)
			.then((data) => {
				node.data["links"] = data["links"];
				setSelectedNode(node.data);
				setUnpublishModalConfirm(true);
			})
			.catch((error) => {
				setSelectedNode(node.data);
				setUnpublishModalConfirm(true);
			});
	};

	const handleConfirmProductUnpublish = () => {
		productUnpublish(selectedNode.id, selectedNode.type)
			.then((resp) => {
				handleUpdateList("Material despublicado com sucesso!");
				setUnpublishModalConfirm(false);
			})
			.catch(() => {
				toast.error("Erro ao despublicar o material!");
			});
	};
	// publish / unpublish functions

	return (
		<>
			<Container fluid>
				<ContentBody>
					<Row className="mt-3">
						{loading ? (
							<NotFound>
								<Spinner
									animation="border"
									variant="primary"
									style={{
										width: "3rem",
										height: "3rem",
										borderWidth: "0.25em",
										borderRightColor: "transparent",
									}}
								/>
							</NotFound>
						) : (
							<div>
								{/* Filtros de Ativo | Inativo | Todos */}
								<div className="d-flex gap-3 mb-3">
									<FilterButton
										className={
											statusFilter === "" ? "active" : ""
										}
										onClick={() => setStatusFilter(``)}
									>
										Todos
									</FilterButton>
									<FilterButton
										className={
											statusFilter === "active"
												? "active"
												: ""
										}
										onClick={() =>
											setStatusFilter(`active`)
										}
									>
										Ativos
									</FilterButton>
									<FilterButton
										className={
											statusFilter === "inactive"
												? "active"
												: ""
										}
										onClick={() =>
											setStatusFilter(`inactive`)
										}
									>
										Inativos
									</FilterButton>
								</div>

								{/* Tabela */}
								{showEmptyMessage ? (
									<div className="text-center text-grey mt-5">
										<FaFolder
											className="mt-5"
											size={100}
										/>
										<h5 className="text-grey">
											{translation(
												"manager.products.emptyMaterials"
											)}
										</h5>
									</div>
								) : (
									<TreeTableProductsList
										treeFields={treeTableConfig}
										nodes={locationList}
										handleTableSort={handleTableSort}
										sortField={sortField}
										sortOrder={sortOrder}
										loading={isSorting}
										onExpand={handleTableNodeExpanded}
										expandedKeys={expandedKeys}
										setExpandedKeys={setExpandedKeys}
									>
										{userType !== "Parceiro" && (
											<Column
												field="knowledge_area"
												className="d-none-xxs-table"
												header="Áreas de Conhecimento"
												style={{
													textAlign: "center",
												}}
												body={knowledgesTemplate}
												sortable
											></Column>
										)}
										{userType === "Visitante" && (
											<Column
												field="actions"
												className="sm-actions"
												header=""
												style={{
													textAlign: "center",
													width: "4%",
													padding: "0px",
												}}
												body={detailsTemplate}
											></Column>
										)}
										{userType === "Parceiro" && (
											<Column
												field="status"
												className="d-none-xxs-table"
												header="Status"
												style={{
													textAlign: "center",
												}}
												body={publishTemplate}
												sortable
											></Column>
										)}
										{(userType === "Parceiro" ||
											userType === "Assinante") && (
											<Column
												field="actions"
												className="sm-actions"
												header=""
												style={{
													textAlign: "center",
													width: "4%",
													padding: "0px",
												}}
												body={actionTemplate}
											></Column>
										)}
									</TreeTableProductsList>
								)}
							</div>
						)}
					</Row>

					{/* Paginação */}
					<Row className="mt-5 mb-3">
						{!loading && locationList.length > 0 ? (
							<PaginationList
								currentPage={currentPage}
								totalItems={totalItens}
								onPageChange={handlePageChange}
							></PaginationList>
						) : (
							<></>
						)}
					</Row>
				</ContentBody>

				{/* ---------------- */}
				{/* ---- Modais ---- */}
				{/* ---------------- */}

				{/* Modal inactive material */}
				<Modal
					headerTitle="Aviso"
					submitButtonText={translation(
						"manager.products.inactivate"
					)}
					show={showInactivateMaterialModalConfirm}
					onHide={() => {
						setShowInactivateMaterialModalConfirm(false);
					}}
					onSubmit={handleConfirmMaterialInactivate}
					variant="confirm-modal-default"
					loading={loadingModal}
					fullSizeLoader
				>
					<p>
						Tem certeza que deseja inativar o material{" "}
						<b className="span-ellipsis">{selectedNode?.title}?</b>
					</p>
					<p>Todos os links do material serão inativados.</p>
				</Modal>

				{/* Modal delete material */}
				<Modal
					headerTitle="Aviso"
					submitButtonText={translation("manager.products.remove")}
					show={showDeleteMaterialModalConfirm}
					onHide={() => {
						setShowDeleteMaterialModalConfirm(false);
					}}
					onSubmit={handleConfirmMaterialDelete}
					variant="confirm-modal"
					loading={loadingModal}
					fullSizeLoader
				>
					<p>
						Tem certeza que deseja excluir o material{" "}
						<b className="span-ellipsis">{selectedNode?.title}?</b>
					</p>
					<p>Todos os materiais vinculados serão excluidos.</p>
				</Modal>

				{/* Modal inactive link) */}
				<Modal
					headerTitle="Aviso"
					submitButtonText={translation(
						"manager.products.inactivate"
					)}
					show={showInactivateLinkModalConfirm}
					onHide={() => {
						setShowInactivateLinkModalConfirm(false);
					}}
					onSubmit={handleConfirmLinkInactivate}
					variant="confirm-modal-default"
					loading={loadingModal}
					fullSizeLoader
				>
					<p>
						Tem certeza que deseja inativar o link{" "}
						<b className="span-ellipsis">{selectedNode?.title}?</b>
					</p>
					<p>Todos os links serão inativados.</p>
				</Modal>

				{/* Modal delete link */}
				<Modal
					headerTitle="Aviso"
					submitButtonText={translation("manager.products.remove")}
					show={showDeleteLinkModalConfirm}
					onHide={() => {
						setShowDeleteLinkModalConfirm(false);
					}}
					onSubmit={handleConfirmLinkDelete}
					variant="confirm-modal"
					loading={loadingModal}
					fullSizeLoader
				>
					<p>
						Tem certeza que deseja excluir o link{" "}
						<b className="span-ellipsis">{selectedNode?.title}?</b>
					</p>
					<p>Todos os links vinculados serão excluidos.</p>
				</Modal>

				{/* Modal unpublish material */}
				<Modal
					headerTitle="Aviso"
					submitButtonText={translation("manager.products.unpublish")}
					show={showUnpublishModalConfirm}
					onHide={() => {
						setUnpublishModalConfirm(false);
					}}
					onSubmit={handleConfirmProductUnpublish}
					variant="confirm-modal-default"
					loading={loadingModal}
					fullSizeLoader
				>
					<p>
						Tem certeza que deseja despublicar o material{" "}
						<b className="span-ellipsis">{selectedNode?.title}?</b>
					</p>
					{selectedNode &&
					selectedNode.links &&
					selectedNode.links >= 0 ? (
						<>
							<p>
								O material está sendo utilizado por{" "}
								{selectedNode?.links} usuários
							</p>
						</>
					) : (
						<></>
					)}
				</Modal>
			</Container>
		</>
	);
};
